// src/pages/CategoryHub.js
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MapPin, Users, Sun, Moon } from 'lucide-react';
import { 
  Breadcrumb, 
  BreadcrumbList, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbPage, 
  BreadcrumbSeparator 
} from '../components/ui/breadcrumb';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Meteors }from '../components/ui/meteors';

const API_URL = process.env.REACT_APP_API_URL;

// Complete descriptions matched by exact category.name
const categoryDescriptions = {
  "Academic Writing & Online Writing": "Professional academic writing, research papers, assignments and online content creation. Get reliable writers who deliver quality work on time.",
  
  "Accounting, Bookkeeping & Tax Specialist": "Trusted accountants, bookkeepers and tax specialists for businesses and individuals. Accurate bookkeeping, VAT returns and tax filing made simple.",
  
  "Airbnb and Accommodation": "Airbnb management, short-term rental hosting and accommodation services. Let experts handle your guests, cleaning and bookings.",
  
  "Art and Tattoos": "Professional tattoo artists and creative artwork services. Custom designs, body art and artistic work done with precision.",
  
  "Babysitting": "Reliable babysitters and childcare providers who are experienced, caring and trustworthy for your little ones.",
  
  "Bakery & Pastry Services(Keki)": "Freshly baked keki, cakes, pastries, mandazi and bread. Find skilled bakers for birthdays, events and daily orders across Kenya.",
  
  "Borehole Technical Services": "Expert borehole drilling, maintenance and water solutions. Reliable technicians for boreholes across Kenya.",
  
  "Caretaker, & House n Domestic Help(MamaFua)": "Trusted mama fua, house help and caretakers for cleaning, laundry and general home management.",
  
  "Carpentry/Carpenter(mbao)": "Skilled carpenters (fundi mbao) for furniture, doors, windows and custom woodwork.",
  
  "Catering and Event Meal handling(Chakula)": "Professional catering and event meal services (chakula). Perfect for weddings, parties and corporate events.",
  
  "Chef and professional cooking services(Upishi, kupika)": "Experienced chefs offering upishi and professional cooking services for homes and events.",
  
  "Construction Services(Mjengo, mtu wa mkono)": "Reliable mjengo and construction services. Fundi wa ujenzi for homes, extensions and renovations.",
  
  "Custom Crafts and Designs": "Handcrafted items, custom designs and artisanal work tailored to your specifications.",
  
  "Data Analytics": "Professional data analysis, reporting and business intelligence services.",
  
  "Digital Marketing": "Expert digital marketing, social media management and online advertising campaigns.",
  
  "DJ and Club entertainment": "Professional DJs and entertainment services for parties, weddings and events.",
  
  "Driving Services(Dereva)": "Reliable dereva (drivers) for personal, delivery or corporate transport needs.",
  
  "Dry Cleaners and Professional Carpet Washing": "Professional dry cleaning and carpet/deep cleaning services.",
  
  "Electrical Services(Electrician/Stima)": "Qualified electricians (fundi stima) for wiring, repairs, installations and solar solutions.",
  
  "EmCee & Ceremony Control": "Professional emcees and masters of ceremony for events and functions.",
  
  "Errands and Delivery(Mzigo)": "Fast errands and delivery services (mzigo). Get anything delivered quickly and safely.",
  
  "Gardening & Landscaping(Kulima, kupanda, kupalilia)": "Expert gardening, landscaping, planting and lawn maintenance services.",
  
  "General Services": "Miscellaneous and general services not covered in specific categories. Flexible solutions for various needs.",
  
  "Glazier, Glass & Mirrors": "Professional glaziers for glass, mirrors, windows and aluminium works.",
  
  "Graphic Design, T-shirt printing, & Creative Posters": "Graphic design, T-shirt printing, branding and creative poster services.",
  
  "Haircuts, Beauty & Salon(Kinyozi, Makeup, Nails)": "Barbers (kinyozi), makeup artists, nails and full beauty salon services.",
  
  "Interior Design": "Professional interior design and space planning for homes and offices.",
  
  "Internet Installation & GSM Repair": "Internet installation, WiFi setup and GSM/phone repair services.",
  
  "Journalism Media and Entertainment": "Journalism, media production and entertainment services.",
  
  "Land Surveying": "Professional land surveying and mapping services.",
  
  "Laptop & Appliance Repair(simu, laptop, gadgets, computer)": "Laptop, phone (simu), appliance and gadget repair services.",
  
  "Laundry and General Cleaning(Mamafua, Usafi)": "Laundry and general cleaning services (mamafua & usafi).",
  
  "Loans and Lending Services": "Loans, lending and financial support services.",
  
  "Loctitian & Dreadlocks Retouch": "Professional loctitian and dreadlocks retouch services.",
  
  "Make Up Artist(Makeup, MUA)": "Skilled makeup artists (MUA) for events, weddings and photoshoots.",
  
  "Massage, Spa and Grooming": "Relaxing massage, spa and grooming services.",
  
  "Mechanic Services": "Professional mechanic services for cars, motorcycles and heavy vehicles.",
  
  "Mechanical Engineering": "Mechanical engineering and technical consultancy services.",
  
  "Moving & Transport(Kuhama, Mzigo)": "Moving and transport services (kuhama & mzigo).",
  
  "Music And Sound Production": "Music production, sound engineering and recording services.",
  
  "Nail Artist, Manicure & Pedicure(Makucha)": "Nail artists offering manicure, pedicure and nail extensions.",
  
  "Painting/Painter(rangi)": "Professional painters (fundi rangi) for interior and exterior painting.",
  
  "Personal Assistant(PA)": "Reliable personal assistants and executive support services.",
  
  "Pest Control(Wadudu-Mende, kunguni)": "Effective pest control for mende, kunguni and other pests.",
  
  "Phone Repair(simu)": "Fast and reliable phone (simu) repair services.",
  
  "Photography Services(Picha)": "Professional photography and videography services.",
  
  "Plumbing/Plumber(maji)": "Qualified plumbers (fundi maji) for all plumbing and drainage needs.",
  
  "Private Nursing & Caregiving": "Private nursing and professional caregiving services.",
  
  "Professional Accounting & Bookkeeping": "Expert accounting and bookkeeping services.",
  
  "Professional Law and Legal Services": "Professional legal and law firm services.",
  
  "Radiography and Imaging": "Radiography, medical imaging and diagnostic services.",
  
  "Real Estate Agency": "Trusted real estate agency and property management services.",
  
  "Security And Surveillance": "Security services, guards and surveillance system installation.",
  
  "Sign Language Interpretation": "Professional sign language interpretation services.",
  
  "Software & Web Development": "Software development, web design and digital solutions.",
  
  "Tailoring & Fashion(Nguo)": "Professional tailoring and fashion design services (nguo).",
  
  "Tutoring & Lessons(mwalimu, tutor)": "Experienced tutors and teachers (mwalimu) for all subjects and levels.",
  
  "Veterinary and Animal Care": "Veterinary services and animal healthcare.",
  
  "Videography Services": "Professional videography and film production services.",
  
  "VISA Application Assistant": "Visa application assistance and documentation support.",
  
  "Water Delivery & Exhauster Services(maji)": "Water delivery and exhauster (septic) services.",
  
  "Waterproofing Services": "Professional waterproofing and damp-proofing solutions.",
  
  "Web & Tech Support": "Web development and technical support services."
};

export default function CategoryHub() {
  const { slug } = useParams();

  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true' ||
           document.documentElement.classList.contains('dark');
  });

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  const { data: category, isLoading, error } = useQuery({
    queryKey: ['category', slug],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/servicecategory/${slug}/`);
      return res.data;
    },
  });

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading category...</div>;
  }

  if (error || !category) {
    return <div className="min-h-screen flex items-center justify-center text-destructive">Category not found</div>;
  }

  // Match using exact category.name (most reliable)
  const customDescription = categoryDescriptions[category.name] || 
    `Find trusted ${category.name.toLowerCase()} professionals across Kenya with SErraND.`;

  const minCharge = category.default_minimum_charge 
    ? `From KES ${parseFloat(category.default_minimum_charge).toLocaleString()}` 
    : "Price varies by service";

  const pageTitle = `${category.name} Services in Kenya | Hire Trusted Professionals - SErraND`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={customDescription} />
        <link rel="canonical" href={`https://www.serrand.org/categories/${category.slug}`} />
      </Helmet>

      <Meteors count={40} angle={220} />

      <div className="relative z-10 min-h-screen bg-background pt-8 pb-16">
        {/* Dark Mode Toggle */}
        <div className="max-w-7xl mx-auto px-4 flex justify-end mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-full"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 mb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/categories">Categories</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{category.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Hero with Custom Description */}
        <section className="max-w-5xl mx-auto px-4 text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
            {category.name} Services
          </h1>
          <p className="text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {customDescription}
          </p>

          <div className="mt-8 inline-flex items-center gap-2 bg-muted rounded-full px-6 py-2.5 text-sm font-medium">
            Usually starts <span className="text-primary">{minCharge}</span>
          </div>
        </section>

        {/* Locations */}
        <section className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-semibold mb-8 flex items-center gap-3">
            <MapPin className="h-8 w-8 text-primary" />
            Popular Locations for {category.name}
          </h2>

          {category.popular_locations && category.popular_locations.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {category.popular_locations.map((loc) => (
                <Link key={loc.slug} to={`/categories/${category.slug}/${loc.slug}`}>
                  <Card className="group h-full hover:shadow-lg transition-all hover:-translate-y-1 border hover:border-primary/30">
                    <CardHeader>
                      <CardTitle className="text-xl group-hover:text-primary transition-colors">
                        {loc.name}
                      </CardTitle>
                      <CardDescription>{category.name} services in {loc.name}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{loc.count} providers</span>
                        </div>
                        <span className="text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                          View providers <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">No popular locations available for this category yet.</p>
            </Card>
          )}
        </section>

        {/* Bottom CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center mt-20">
          <Button asChild size="lg" className="rounded-full">
            <Link to="/categories">Browse All Categories</Link>
          </Button>
        </section>
      </div>
    </>
  );
}
